import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, type Article } from "../lib/api.ts";
import { statusBadgeClass, statusLabel, relativeTime, truncate } from "../lib/utils.ts";

const STATUS_TABS = [
  { value: "pending_review",     label: "Needs Review" },
  { value: "pending_rewrite",    label: "Pending Rewrite" },
  { value: "rewriting",           label: "Rewriting" },
  { value: "content_incomplete", label: "Incomplete" },
  { value: "approved",           label: "Approved" },
  { value: "published",          label: "Published" },
  { value: "rejected",           label: "Rejected" },
  { value: "dismissed",          label: "Dismissed" },
  { value: "error",              label: "Errors" },
  { value: "",                   label: "All" },
];

export default function ReviewQueue() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeStatus = searchParams.get("status") ?? "pending_review";
  const sortBy = searchParams.get("sort") ?? "created_at";
  const sortOrder = searchParams.get("order") ?? "desc";
  const [articles, setArticles] = useState<Article[]>([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);

  const loadArticles = useCallback(() => {
    setLoading(true);
    const params: Record<string, string> = { limit: "50", sort: sortBy, order: sortOrder };
    if (activeStatus) params.status = activeStatus;
    api.articles.list(params)
      .then(r => { setArticles(r.articles); setTotal(r.total); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [activeStatus, sortBy, sortOrder]);

  useEffect(() => { loadArticles(); }, [loadArticles]);

  async function handleApprove(id: number, e: React.MouseEvent) {
    e.preventDefault();
    await api.articles.approve(id);
    loadArticles();
  }

  async function handleReject(id: number, e: React.MouseEvent) {
    e.preventDefault();
    const note = prompt("Rejection note (optional):");
    if (note === null) return; // cancelled
    await api.articles.reject(id, note);
    loadArticles();
  }

  async function handleRewrite(id: number, e: React.MouseEvent) {
    e.preventDefault();
    await api.articles.rewrite(id);
    loadArticles();
  }

  return (
    <div className="p-6">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-gray-900">Review Queue</h1>
        <p className="text-gray-500 text-sm mt-1">{total} article{total !== 1 ? "s" : ""}</p>
      </div>

      {/* Status tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-3 overflow-x-auto">
        {STATUS_TABS.map(tab => (
          <button
            key={tab.value}
            onClick={() => setSearchParams(tab.value ? { status: tab.value, sort: sortBy, order: sortOrder } : { sort: sortBy, order: sortOrder })}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeStatus === tab.value
                ? "border-brand-500 text-brand-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Sort controls */}
      <div className="flex items-center gap-3 mb-4">
        <label className="text-xs text-gray-500">Sort by:</label>
        <select
          value={`${sortBy}-${sortOrder}`}
          onChange={e => {
            const [newSort, newOrder] = e.target.value.split("-");
            setSearchParams({ status: activeStatus, sort: newSort, order: newOrder });
          }}
          className="input text-xs py-1 pr-6"
        >
          <option value="created_at-desc">Date (newest)</option>
          <option value="created_at-asc">Date (oldest)</option>
          <option value="original_published_at-desc">Article date (newest)</option>
          <option value="original_published_at-asc">Article date (oldest)</option>
          <option value="feed_name-asc">Feed (A-Z)</option>
          <option value="feed_name-desc">Feed (Z-A)</option>
        </select>
      </div>

      {loading ? (
        <div className="text-gray-400 text-sm">Loading…</div>
      ) : articles.length === 0 ? (
        <div className="card p-8 text-center text-gray-400 text-sm">No articles</div>
      ) : (
        <div className="space-y-3">
          {articles.map(article => (
            <Link
              key={article.id}
              to={`/articles/${article.id}`}
              className="card p-4 block hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={statusBadgeClass(article.status)}>
                      {statusLabel(article.status)}
                    </span>
                    <span className="badge-gray">{article.feed_name ?? `Feed ${article.feed_id}`}</span>
                    <span className="badge-gray">{article.ghost_instance ?? "news"}</span>
                    {article.processing_tier === "batch" && (
                      <span className="badge-purple">Batch</span>
                    )}
                  </div>
                  <h3 className="font-medium text-gray-900 truncate">
                    {article.rewritten_title ?? article.original_title}
                  </h3>
                  {article.content_fetch_error && (
                    <p className="text-xs text-red-600 mt-1">{truncate(article.content_fetch_error, 100)}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">{relativeTime(article.created_at)}</p>
                </div>

                {/* Quick action buttons */}
                <div className="flex gap-2 flex-shrink-0" onClick={e => e.preventDefault()}>
                  {article.status === "pending_review" && (
                    <>
                      <button
                        onClick={e => handleApprove(article.id, e)}
                        className="btn btn-sm bg-green-600 text-white hover:bg-green-700 focus:ring-green-500"
                      >
                        Approve
                      </button>
                      <button
                        onClick={e => handleReject(article.id, e)}
                        className="btn btn-sm bg-red-100 text-red-700 hover:bg-red-200 focus:ring-red-400"
                      >
                        Reject
                      </button>
                    </>
                  )}
                  {(article.status === "error" || article.status === "content_incomplete") && (
                    <button
                      onClick={e => handleRewrite(article.id, e)}
                      className="btn btn-sm bg-blue-100 text-blue-700 hover:bg-blue-200 focus:ring-blue-400"
                    >
                      Retry
                    </button>
                  )}
                  {article.status === "pending_rewrite" && (
                    <button
                      onClick={e => handleRewrite(article.id, e)}
                      className="btn btn-sm bg-indigo-100 text-indigo-700 hover:bg-indigo-200 focus:ring-indigo-400"
                    >
                      Rewrite
                    </button>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
